import React from 'react';
import { scrollToTop } from '../utils/scrollUtils';
import { 
  Container, 
  Typography, 
  Grid, 
  Box, 
  Button, 
  useTheme, 
  alpha, 
  Chip, 
  Divider,
  Card,
  CardContent,
  useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SchoolIcon from '@mui/icons-material/School';
import { motion } from 'framer-motion';

function Course() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDarkMode = theme.palette.mode === 'dark';

  const coursesData = [
    {
      title: "Fall Prevention Protocol",
      description:
        "Learn essential techniques and best practices for preventing falls and ensuring patient safety in various care settings.",
      duration: "2 hours",
      level: "Intermediate",
      path: "/courses/fall-prevention",
      color: theme.palette.primary.main,
      secondaryColor: alpha(theme.palette.primary.main, 0.08),
      topics: [
        "Risk Assessment",
        "Environmental Safety",
        "Patient Monitoring",
        "Emergency Response",
        "Documentation",
        "Impact of Falls",
      ],
    },
    {
      title: "Choking Management Protocol",
      description:
        "Essential steps to quickly identify and respond to choking incidents, ensuring effective and timely intervention.",
      duration: "2 hours",
      level: "Intermediate",
      path: "/courses/choking-management",
      color: theme.palette.primary.main,
      secondaryColor: alpha(theme.palette.primary.main, 0.08),
      topics: [
        "Initial Assessment and Recognition",
        "Immediate Response",
        "Notification and Communication",
        "Post-Choking Monitoring and Reassessment",
        "Documentation, Reporting, and Analysis",
        "Preventive Measures and Patient Education"
      ],
    },
    {
      title: "Basic Life Support (BLS) and CPR Training",
      description:
        "Comprehensive training in Basic Life Support and CPR techniques, including emergency response protocols and post-resuscitation care.",
      duration: "4 hours",
      level: "Essential",
      path: "/courses/bls-cpr",
      color: theme.palette.primary.main,
      secondaryColor: alpha(theme.palette.primary.main, 0.08),
      topics: [
        "CPR Fundamentals",
        "Emergency Response Protocols",
        "Choking Management",
        "AED Usage",
        "Post-Resuscitation Care",
        "Documentation and Reporting"
      ],
    },
  ];

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6 } }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const pulseAnimation = {
    scale: [1, 1.02, 1],
    transition: { duration: 2, repeat: Infinity }
  };

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box 
        component={motion.div}
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        sx={{
          position: 'relative',
          background: isDarkMode ? 'grey.900' : 'transparent',
          py: { xs: 6, md: 10 },
          mb: 0,
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={7}>
              <motion.div variants={fadeInUp}>
                <Typography 
                  variant="h2" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    mb: 2,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                    backgroundClip: 'text',
                    textFillColor: 'transparent',
                    lineHeight: 1.2
                  }}
                >
                  Caregiver Training Courses
                </Typography>
                <Typography 
                  variant="h5" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 4,
                    maxWidth: '600px',
                    lineHeight: 1.6
                  }}
                >
                  Comprehensive training modules for caregivers of all experience levels.
                </Typography>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  width: { xs: '200px', md: '280px' },
                  height: { xs: '200px', md: '280px' },
                  borderRadius: '50%',
                  background: theme.palette.common.white,
                }}
              >
                <SchoolIcon sx={{ fontSize: '4rem', color: theme.palette.primary.main }} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Divider */}
      <Container maxWidth="lg">
        <Divider sx={{ 
          mb: 8, 
          mt: 0,
          backgroundColor: theme => alpha(theme.palette.divider, 0.6),
          height: '1px'
        }} />
      </Container>

      {/* Current Courses Section */}
      <Container maxWidth="lg" sx={{ mb: 10 }}>
        <Box 
          component={motion.div}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          sx={{ mb: 6, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          <Typography 
            variant="overline" 
            sx={{ 
              mb: 3,
              display: 'block',
              fontWeight: 700, 
              letterSpacing: '2px', 
              color: theme.palette.primary.main,
              opacity: 0.9
            }}
          >
            CURRENT OFFERINGS
          </Typography>
          <Typography 
            variant="h4" 
            component="h2" 
            sx={{ 
              fontWeight: 700,
              mb: 2, 
              textAlign: 'center'
            }}
          >
            Available Courses
          </Typography>
          <Typography 
            color="text.secondary" 
            sx={{ 
              maxWidth: 700, 
              mb: 3, 
              textAlign: 'center',
              mx: 'auto'
            }}
          >
            Each course is designed by healthcare professionals to deliver practical,
            <br />evidence-based training.
          </Typography>
        </Box>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          <Grid container spacing={4}>
            {coursesData.map((course, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div variants={fadeInUp} whileHover={{ y: -8, transition: { duration: 0.3 } }}>
                  <Card
                    elevation={0}
                    onClick={() => {
                      navigate(course.path);
                      scrollToTop();
                    }}
                    sx={{
                      borderRadius: '20px',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      backgroundColor: theme.palette.background.paper,
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderColor: alpha(theme.palette.primary.light, 0.7),
                      },
                    }}
                  >
                    {/* Course Color Bar */}
                    <Box 
                      sx={{ 
                        height: '6px', 
                        background: `linear-gradient(90deg, ${course.color} 0%, ${theme.palette.secondary.main} 100%)`,
                      }} 
                    />
                    
                    <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      {/* Course Header */}
                      <Box sx={{ p: 4, pb: 2 }}>
                        <Typography 
                          variant="h5" 
                          component="h3" 
                          sx={{ 
                            fontWeight: 700, 
                            mb: 1.5, 
                            color: course.color,
                            lineHeight: 1.3,
                            fontSize: { xs: '1.4rem', md: '1.6rem' }
                          }}
                        >
                          {course.title}
                        </Typography>
                        
                        {/* Course Metadata */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Chip 
                            label={course.level} 
                            size="small"
                            sx={{
                              fontWeight: 600,
                              bgcolor: alpha(course.color, 0.15),
                              color: course.color,
                              borderRadius: '20px',
                              height: '24px'
                            }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon sx={{ color: alpha(course.color, 0.8), fontSize: 18 }} />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'text.secondary',
                                fontWeight: 500
                              }}
                            >
                              {course.duration}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Course Description */}
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                          {course.description}
                        </Typography>
                      </Box>

                      <Divider />

                      {/* Topics List */}
                      <Box sx={{ p: 4, pt: 3, flexGrow: 1 }}>
                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                          What you'll learn:
                        </Typography>
                        <Box sx={{ mb: 4 }}>
                          {course.topics.map((topic, topicIndex) => (
                            <Box 
                              key={topicIndex}
                              sx={{ 
                                display: 'flex', 
                                gap: 1.5, 
                                mb: 1.5, 
                                alignItems: 'flex-start' 
                              }}
                            >
                              <CheckCircleOutlineIcon 
                                sx={{ 
                                  color: course.color, 
                                  fontSize: 20, 
                                  mt: 0.3
                                }} 
                              />
                              <Typography 
                                variant="body2"
                                color="text.secondary"
                              >
                                {topic}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>

                      {/* Action Button */}
                      <Box sx={{ p: 4, pt: 0 }}>
                        <Button
                          variant="contained"
                          fullWidth
                          endIcon={<ArrowForwardIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(course.path);
                            scrollToTop();
                          }}
                          sx={{
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            borderRadius: '50px',
                            backgroundColor: course.color,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: alpha(course.color, 0.9),
                              transform: 'translateY(-3px)',
                              boxShadow: `0 8px 20px ${alpha(course.color, 0.3)}`,
                            },
                          }}
                        >
                          Start Course
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>

      {/* Divider between sections */}
      <Container maxWidth="lg" sx={{ mb: 6 }}>
        <Divider sx={{ 
          backgroundColor: theme => alpha(theme.palette.divider, 0.6),
          height: '1px'
        }} />
      </Container>

      {/* Coming Soon Section */}
      <Box 
        component={motion.div}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeIn}
        sx={{ 
          py: { xs: 6, md: 10 },
          mb: 6,
          bgcolor: isDarkMode 
            ? alpha(theme.palette.background.paper, 0.4) 
            : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.light, 0.07)} 100%)`,
        }}
      >
        <Container maxWidth="lg">
          <Box 
            sx={{ 
              mb: 6, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center' 
            }}
          >
            <Typography 
              variant="overline" 
              sx={{ 
                mb: 3,
                display: 'block',
                fontWeight: 700, 
                letterSpacing: '2px', 
                color: theme.palette.secondary.main,
                opacity: 0.9
              }}
            >
              COMING SOON
            </Typography>
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                fontWeight: 700,
                mb: 2, 
                textAlign: 'center'
              }}
            >
              Future Courses
            </Typography>
            <Typography 
              color="text.secondary" 
              sx={{ 
                maxWidth: 700, 
                mb: 3, 
                textAlign: 'center',
                mx: 'auto'
              }}
            >
              We're working on developing more comprehensive training modules to enhance your caregiving skills. 
              Stay tuned for these exciting new courses!
            </Typography>
          </Box>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <motion.div variants={fadeInUp} whileHover={{ y: -5, transition: { duration: 0.2 } }}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      textAlign: 'center', 
                      borderRadius: '20px',
                      p: 4,
                      border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      backgroundColor: theme.palette.background.paper,
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderColor: alpha(theme.palette.primary.light, 0.7),
                      },
                    }}
                  >
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        width: '100%', 
                        height: '6px', 
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)` 
                      }} 
                    />
                    <Box sx={{ 
                      width: 70, 
                      height: 70, 
                      borderRadius: '50%',
                      background: theme.palette.grey[200],
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      mb: 2
                    }}>
                      <Typography variant="h5" color={theme.palette.primary.main} fontWeight={700}>Q2</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1, fontSize: '1.2rem' }}>
                      Medication Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, height: 72, display: 'flex', alignItems: 'center' }}>
                      Learn proper medication administration, tracking, and side effect monitoring.
                    </Typography>
                    <Chip
                      label="Coming April 2025"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        borderRadius: '20px',
                        px: 1
                      }}
                    />
                  </Card>
                </motion.div>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <motion.div variants={fadeInUp} whileHover={{ y: -5, transition: { duration: 0.2 } }}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      textAlign: 'center', 
                      borderRadius: '20px',
                      p: 4,
                      border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      backgroundColor: theme.palette.background.paper,
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderColor: alpha(theme.palette.primary.light, 0.7),
                      },
                    }}
                  >
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        width: '100%', 
                        height: '6px', 
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)` 
                      }} 
                    />
                    <Box sx={{ 
                      width: 70, 
                      height: 70, 
                      borderRadius: '50%',
                      background: theme.palette.grey[200],
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      mb: 2
                    }}>
                      <Typography variant="h5" color={theme.palette.primary.main} fontWeight={700}>Q2</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1, fontSize: '1.2rem' }}>
                      Dementia Care
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, height: 72, display: 'flex', alignItems: 'center' }}>
                      Specialized techniques for caring for patients with Alzheimer's and other forms of dementia.
                    </Typography>
                    <Chip
                      label="Coming May 2025"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        borderRadius: '20px',
                        px: 1
                      }}
                    />
                  </Card>
                </motion.div>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <motion.div variants={fadeInUp} whileHover={{ y: -5, transition: { duration: 0.2 } }}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      textAlign: 'center', 
                      borderRadius: '20px',
                      p: 4,
                      border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'hidden',
                      backgroundColor: theme.palette.background.paper,
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.2)}`,
                        borderColor: alpha(theme.palette.primary.light, 0.7),
                      },
                    }}
                  >
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        width: '100%', 
                        height: '6px', 
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)` 
                      }} 
                    />
                    <Box sx={{ 
                      width: 70, 
                      height: 70, 
                      borderRadius: '50%',
                      background: theme.palette.grey[200],
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      mb: 2
                    }}>
                      <Typography variant="h5" color={theme.palette.primary.main} fontWeight={700}>Q3</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1, fontSize: '1.2rem' }}>
                      Mobility Assistance
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, height: 72, display: 'flex', alignItems: 'center' }}>
                      Safe techniques for transfers, positioning, and ambulation assistance.
                    </Typography>
                    <Chip
                      label="Coming June 2025"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        borderRadius: '20px',
                        px: 1
                      }}
                    />
                  </Card>
                </motion.div>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
}

export default Course;
